import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Star, MessageCircle, GitMerge } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, gbp } from "../lib/api";
import { useOrder } from "../context/OrderContext";
import { QtyControl, isFractionalUnit } from "../components/order/ProductCard";
import SwipeToDeleteRow from "../components/order/SwipeToDeleteRow";
import MergeOrdersDialog from "../components/order/MergeOrdersDialog";
import { useIsTouch } from "../hooks/useIsTouch";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";

export default function OrderReview() {
  const { items, note, orderId, updateNote, setQty, setQtyKeepZero, removeItem, restoreItem, swapSupplier, flush, clear, loaded, loadServerOrder } = useOrder();
  const navigate = useNavigate();
  const isTouch = useIsTouch();
  const [order, setOrder] = useState(null);
  const [showSavings, setShowSavings] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState([]);
  const [mergeOpen, setMergeOpen] = useState(false);

  useEffect(() => {
    if (!loaded) return;
    let alive = true;
    (async () => { await flush(); if (orderId) api.get(`/ordering/orders/${orderId}`).then((r) => alive && setOrder(r.data)).catch(() => {}); })();
    return () => { alive = false; };
  }, [items, note, orderId, loaded, flush]);

  const bySupplier = {};
  const supplierIdOf = {};
  // Show the basket IMMEDIATELY from the local draft; when the server GET returns we swap each row
  // for its richer server copy (display_name, supplier_id for Merge, canonical price snapshot) but
  // the on-screen content never disappears while the server is working. This is what killed mobile
  // UX before: the review page waited on both `flush()` + the following GET round-trip (often 2–3 s
  // on cellular) and showed nothing except "Saving…" in between.
  const serverById = {};
  (order?.items || []).forEach((i) => { serverById[i.supplier_product_id] = i; });
  const rows = Object.entries(items).map(([sid, v]) => serverById[sid] || {
    supplier_product_id: sid,
    description: v.description,
    display_name: v.description,
    supplier_name: v.supplier_name || "Unknown supplier",
    supplier_id: null,
    unit: v.unit,
    purchase_unit: v.purchase_unit,
    snapshot: { price: v.price },
  });
  rows.forEach((i) => { (bySupplier[i.supplier_name] ||= []).push(i); supplierIdOf[i.supplier_name] = i.supplier_id; });
  const suppliers = Object.keys(bySupplier);
  const savings = order?.savings || [];
  // Only suppliers that still have a line with quantity > 0 can take part in a merge.
  const mergeable = new Set(suppliers.filter((n) => bySupplier[n].some((i) => (items[i.supplier_product_id]?.quantity || 0) > 0)).map((n) => supplierIdOf[n]));
  const selectedIds = selected.filter((id) => mergeable.has(id));
  const toggleSupplier = (id, on) => setSelected((prev) => (on ? [...new Set([...prev, id])] : prev.filter((x) => x !== id)));

  const onMerged = (data) => { loadServerOrder(data); setOrder(data); setSelected([]); };

  const handleDelete = (i) => {
    const sid = i.supplier_product_id;
    const snapshot = items[sid];
    removeItem(sid);
    toast("Removed from basket", {
      action: { label: "Undo", onClick: () => restoreItem(sid, snapshot) },
      duration: 5000,
    });
  };

  const send = async () => {
    setBusy(true);
    try { await flush(); await api.post(`/ordering/orders/${orderId}/prepare`); clear(); navigate(`/orders/${orderId}?send=1`); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  if (!loaded) return <p className="text-sm text-slate-500">Loading…</p>;
  if (!Object.keys(items).length) return <div className="text-center py-16" data-testid="review-empty"><p className="text-slate-600">Your order is empty.</p><Button asChild className="mt-4 bg-emerald-600"><Link to="/order">Add products</Link></Button></div>;

  return (
    <div data-testid="order-review-page" className="max-w-2xl pb-32">
      <Link to="/order" className="inline-flex items-center gap-1 text-sm text-slate-500 mb-3" data-testid="back-to-order"><ArrowLeft className="h-4 w-4" /> Keep adding</Link>
      <h1 className="text-2xl font-bold text-slate-900">Your Order</h1>
      <p className="text-sm text-slate-500 mt-1" data-testid="supplier-count-note">{order ? `Your order will be sent to ${suppliers.length} supplier${suppliers.length === 1 ? "" : "s"}` : "Saving…"}</p>

      {savings.length > 0 && (
        <div className="mt-4 bg-amber-50 border border-amber-300 rounded-xl p-4" data-testid="savings-banner">
          <p className="font-semibold text-amber-900 flex items-center gap-1.5"><Star className="h-4 w-4 fill-amber-500 text-amber-500" /> You could save {gbp(order.potential_saving)}</p>
          <p className="text-sm text-amber-800">{savings.length} cheaper option{savings.length > 1 ? "s" : ""} found</p>
          <div className="mt-2 flex gap-2"><Button size="sm" variant="outline" onClick={() => setShowSavings((v) => !v)} data-testid="view-savings-button">{showSavings ? "Hide" : "View Savings"}</Button></div>
          {showSavings && (
            <ul className="mt-3 space-y-2">
              {savings.map((s) => (
                <li key={s.supplier_product_id} className="bg-white rounded-lg p-3 text-sm flex items-center justify-between gap-2" data-testid={`saving-${s.supplier_product_id}`}>
                  <div className="min-w-0"><p className="font-medium truncate">{s.description}</p><p className="text-xs text-slate-500">{s.current_supplier} {gbp(s.current_price)} → {s.recommended.supplier_name} {gbp(s.recommended.price)} · save {gbp(s.save_total)}</p></div>
                  <Button size="sm" className="bg-emerald-600 shrink-0" onClick={() => swapSupplier(s.supplier_product_id, s.recommended, items[s.supplier_product_id]?.quantity || 1)} data-testid={`apply-saving-${s.supplier_product_id}`}>Switch</Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {suppliers.length >= 2 && order && (
        <div className="mt-4 rounded-xl border border-slate-200 bg-white px-4 py-3 flex items-center justify-between gap-3" data-testid="merge-bar">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900 flex items-center gap-1.5"><GitMerge className="h-4 w-4 text-emerald-600" /> Merge Orders</p>
            <p className="text-xs text-slate-500" data-testid="merge-bar-hint">{selectedIds.length < 2 ? `Tick two or more suppliers to compare ordering everything from one of them${selectedIds.length === 1 ? " (1 selected)" : ""}` : `${selectedIds.length} suppliers selected`}</p>
          </div>
          <Button size="sm" disabled={selectedIds.length < 2 || !orderId} onClick={() => setMergeOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 shrink-0" data-testid="merge-orders-button">Merge Orders{selectedIds.length >= 2 ? ` (${selectedIds.length})` : ""}</Button>
        </div>
      )}

      <div className="mt-4 space-y-4">
        {suppliers.map((name) => (
          <section key={name} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`review-supplier-${name}`}>
            <h2 className="px-4 py-2.5 bg-slate-50 font-semibold text-slate-900 text-sm flex items-center gap-3">
              {suppliers.length >= 2 && order && (
                <label className="flex items-center cursor-pointer -ml-1 p-1" aria-label={`Select ${name} for merge`}>
                  <Checkbox checked={selectedIds.includes(supplierIdOf[name])} disabled={!mergeable.has(supplierIdOf[name])} onCheckedChange={(v) => toggleSupplier(supplierIdOf[name], !!v)} className="h-5 w-5" data-testid={`merge-select-${supplierIdOf[name]}`} />
                </label>
              )}
              <span className="flex-1 truncate">{name}</span>
            </h2>
            <ul className="divide-y divide-slate-100">
              {bySupplier[name].map((i) => (
                <li key={i.supplier_product_id} data-testid={`review-item-${i.supplier_product_id}`}>
                  <SwipeToDeleteRow enabled={isTouch} onDelete={() => handleDelete(i)} testId={`swipe-${i.supplier_product_id}`} deleteLabel={`Remove ${i.display_name || i.description} from basket`}>
                    <div className="px-4 py-2.5 flex items-center gap-3 bg-white">
                      <div className="min-w-0 flex-1"><p className="text-sm font-medium truncate">{i.display_name || i.description}{i.display_name && i.display_name !== i.description ? <span className="text-xs text-slate-400 font-normal"> · {i.description}</span> : null}</p><p className="text-xs text-slate-500 font-mono" data-testid={`review-price-${i.supplier_product_id}`}>{gbp(i.snapshot.price)} per {i.unit ? (i.unit.length <= 5 && i.unit === i.unit.toUpperCase() ? i.unit : i.unit.toLowerCase()) : "unit"}</p></div>
                      <QtyControl value={items[i.supplier_product_id]?.quantity || 0} onChange={(v) => (isTouch ? setQtyKeepZero : setQty)({ supplier_product_id: i.supplier_product_id, description: i.description, supplier_name: i.supplier_name, price: i.snapshot.price, unit: i.unit, default_purchase_unit: i.purchase_unit }, v)} testId={`review-qty-${i.supplier_product_id}`} allowFraction={isFractionalUnit(i.unit)} />
                    </div>
                  </SwipeToDeleteRow>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      <div className="mt-4 space-y-1">
        <label className="text-sm font-medium text-slate-700">Note for suppliers <span className="text-slate-400 font-normal">(optional)</span></label>
        <Textarea value={note} onChange={(e) => updateNote(e.target.value)} placeholder="e.g. Please deliver before 10am" className="bg-white" data-testid="order-note" />
      </div>

      {order && (
        <div className="mt-4 text-sm text-slate-600 bg-white border border-slate-200 rounded-xl p-4" data-testid="internal-estimate">
          <div className="flex justify-between"><span>Estimated total <span className="text-slate-400">(internal, based on last known prices)</span></span><span className="font-mono font-semibold text-slate-900">{gbp(order.totals.estimated_total)}</span></div>
          {order.potential_saving > 0 && <div className="flex justify-between text-amber-800 mt-1"><span>Potential saving</span><span className="font-mono">{gbp(order.potential_saving)}</span></div>}
          <p className="text-xs text-slate-400 mt-2">Prices are never included in the WhatsApp message to suppliers.</p>
        </div>
      )}

      <MergeOrdersDialog open={mergeOpen} onOpenChange={setMergeOpen} orderId={orderId} supplierIds={selectedIds} onApplied={onMerged} />

      <div className="fixed bottom-[96px] lg:bottom-6 inset-x-0 lg:left-[260px] px-4 z-30">
        <Button onClick={send} disabled={busy || !order} className="mx-auto w-full max-w-lg h-14 rounded-full bg-emerald-600 hover:bg-emerald-700 text-base font-semibold shadow-xl flex" data-testid="send-order-button"><MessageCircle className="h-5 w-5 mr-2" /> Send Order via WhatsApp</Button>
      </div>
    </div>
  );
}
